import {Source} from "@amplitude/experiment-js-client";
import {add, experiment, flush, getDeviceId, getSessionId, getUserId, Identify, identify, initAll, reset, setUserId, Types} from "@amplitude/unified";
import type {UnifiedOptions} from "@amplitude/unified/lib/cjs/unified";

import {IAmplitudeExperimentVariants} from "../types/IAmplitudeExperimentVariants";
import {defaultAmplitudeLogger, IAmplitudeLogger} from "../types/IAmplitudeLogger";
import {initAmplitudePageLeaveFlush} from "./init_amplitude_page_leave_flush";

type AmplitudeUserPropertyValue = Types.ValidPropertyType | null;

const amplitudeState = {
    isInitialized: false,
    initializationPromise: null as Promise<void> | null,
    pendingUserId: undefined as string | undefined,
    hasPendingUserId: false,
    pendingUserProperties: null as Record<string, AmplitudeUserPropertyValue> | null
};

const applyAmplitudeUserId = (userId: string | undefined): void => setUserId(userId);

const applyAmplitudeUserProperties = (properties: Record<string, AmplitudeUserPropertyValue>): void => {
    const identifyObj = new Identify();

    for (const [key, value] of Object.entries(properties)) {
        if (value === null) {
            identifyObj.unset(key);
            continue;
        }

        identifyObj.set(key, value);
    }

    identify(identifyObj);
};

const flushPendingAmplitudeUserState = (): void => {
    if (!amplitudeState.isInitialized) return;

    if (amplitudeState.hasPendingUserId) {
        applyAmplitudeUserId(amplitudeState.pendingUserId);
        amplitudeState.hasPendingUserId = false;
    }

    if (amplitudeState.pendingUserProperties) {
        applyAmplitudeUserProperties(amplitudeState.pendingUserProperties);
        amplitudeState.pendingUserProperties = null;
    }
};

const ALLOWED_AMPLITUDE_ENVIRONMENTS = ["production", "stage:top", "stage:dev", "stage:ovh", "local"] as const;
const APP_ENVIRONMENT_PROPERTY = "app_environment";
const AMPLITUDE_ENVIRONMENT_PLUGIN_NAME = "app-environment-enrichment";

const CROSS_PSEUDO_ID_PROPERTY = "cross_pseudo_id";
const CROSS_PSEUDO_ID_PLUGIN_NAME = "cross-pseudo-id-enrichment";

let lastCrossPseudoId: string | null = null;
const crossPseudoIdListeners = new Set<(crossPseudoId: string | null) => void>();

// Amplitude session id alone is the session start timestamp (ms), so two devices starting a session
// in the same millisecond would share it - the device id (random UUID) makes the value unique.
const buildCrossPseudoId = (deviceId: string | undefined, sessionId: number | undefined): string | null => {
    if (deviceId === undefined || sessionId === undefined) {
        return null;
    }

    return `${deviceId}_${sessionId}`;
};

const applyCrossPseudoId = (crossPseudoId: string | null): void => {
    const identifyObj = new Identify();
    if (crossPseudoId === null) {
        identifyObj.unset(CROSS_PSEUDO_ID_PROPERTY);
    } else {
        identifyObj.set(CROSS_PSEUDO_ID_PROPERTY, crossPseudoId);
    }
    identify(identifyObj);

    crossPseudoIdListeners.forEach((listener) => listener(crossPseudoId));
};

// Amplitude has no native "session changed" event - the enrichment hook (fired on every
// tracked event) is the only place we can detect a session or device rotation (including it disappearing).
const crossPseudoIdPlugin: Parameters<typeof add>[0] = {
    name: CROSS_PSEUDO_ID_PLUGIN_NAME,
    type: "enrichment",
    async execute(event) {
        const crossPseudoId = buildCrossPseudoId(getDeviceId() ?? undefined, getSessionId() ?? undefined);

        if (crossPseudoId !== lastCrossPseudoId) {
            lastCrossPseudoId = crossPseudoId;
            applyCrossPseudoId(crossPseudoId);
        }

        return event;
    }
};

type TAmplitudeEnvironment = (typeof ALLOWED_AMPLITUDE_ENVIRONMENTS)[number];

interface IInitAmplitudeParams {
    amplitudeApiKey?: string;
    deploymentKey?: string;
    initialVariants?: IAmplitudeExperimentVariants;
    userId?: string;
    isBrowser?: boolean;
    logger?: IAmplitudeLogger;
    environment?: string;
}

const validateAmplitudeEnvironment = (environment: string): TAmplitudeEnvironment => {
    if (ALLOWED_AMPLITUDE_ENVIRONMENTS.includes(environment as TAmplitudeEnvironment)) {
        return environment as TAmplitudeEnvironment;
    }

    throw new Error(`Invalid Amplitude environment "${environment}". Allowed values: ${ALLOWED_AMPLITUDE_ENVIRONMENTS.join(", ")}`);
};

export const initAmplitude = ({
    amplitudeApiKey,
    deploymentKey,
    initialVariants,
    userId,
    isBrowser = false,
    logger: _logger = defaultAmplitudeLogger,
    environment
}: IInitAmplitudeParams): void => {
    if (!amplitudeApiKey) return; // no key, silent exit

    if (process.env.EXEC_ENV !== "browser" && !isBrowser) return;

    // initialize only once
    if (amplitudeState.isInitialized || amplitudeState.initializationPromise) return;

    const validatedEnvironment = environment ? validateAmplitudeEnvironment(environment) : undefined;
    const options: UnifiedOptions = {
        serverZone: "EU",
        analytics: {
            autocapture: true
        }
    };

    if (deploymentKey) {
        options.experiment = {
            deploymentKey,
            initialVariants: initialVariants ?? {},
            source: Source.InitialVariants
        };
    }

    if (validatedEnvironment) {
        const environmentPlugin: Parameters<typeof add>[0] = {
            name: AMPLITUDE_ENVIRONMENT_PLUGIN_NAME,
            type: "enrichment",
            async execute(event) {
                event.event_properties = {
                    ...(event.event_properties ?? {}),
                    [APP_ENVIRONMENT_PROPERTY]: validatedEnvironment
                };

                return event;
            }
        };

        add(environmentPlugin);
    }

    add(crossPseudoIdPlugin);

    initAmplitudePageLeaveFlush();

    amplitudeState.initializationPromise = Promise.resolve(initAll(amplitudeApiKey, options))
        .then(() => {
            amplitudeState.isInitialized = true;
            flushPendingAmplitudeUserState();
        })
        .catch((error) => {
            amplitudeState.initializationPromise = null;
            throw error;
        });

    if (userId) setAmplitudeUserId(userId);

    if (validatedEnvironment) {
        const identifyObj = new Identify();

        identifyObj.set(APP_ENVIRONMENT_PROPERTY, validatedEnvironment);
        identify(identifyObj);
    }
};

export const waitForAmplitudeInitialization = async (): Promise<void> => {
    await amplitudeState.initializationPromise;
};

export const setAmplitudeUserId = (userId: string | undefined): void => {
    amplitudeState.pendingUserId = userId;
    amplitudeState.hasPendingUserId = true;

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserId(userId);
        amplitudeState.hasPendingUserId = false;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserId(userId);
};

export const setAmplitudeUserProperties = (properties: Record<string, AmplitudeUserPropertyValue>): void => {
    amplitudeState.pendingUserProperties = {...(amplitudeState.pendingUserProperties ?? {}), ...properties};

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserProperties(properties);
        amplitudeState.pendingUserProperties = null;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserProperties(properties);
};

export const flushAmplitude = async (): Promise<void> => {
    if (!amplitudeState.isInitialized) return;

    await flush().promise;
};

export const resetAmplitude = (): void => {
    amplitudeState.hasPendingUserId = false;
    amplitudeState.pendingUserProperties = null;

    if (amplitudeState.isInitialized) reset();
};

export const getAmplitudeUserId = (): string | undefined => {
    if (!amplitudeState.isInitialized) return undefined;

    return getUserId() ?? undefined;
};

export const getAmplitudeDeviceId = (): string | undefined => {
    if (!amplitudeState.isInitialized && !amplitudeState.initializationPromise) {
        return undefined;
    }

    return getDeviceId() ?? undefined;
};

export const getAmplitudeSessionId = (): number | undefined => {
    if (!amplitudeState.isInitialized) {
        return undefined;
    }

    return getSessionId() ?? undefined;
};

export const getCrossPseudoId = (): string | undefined => {
    if (!amplitudeState.isInitialized) {
        return undefined;
    }

    return buildCrossPseudoId(getDeviceId() ?? undefined, getSessionId() ?? undefined) ?? undefined;
};

export const onCrossPseudoIdChange = (callback: (crossPseudoId: string | null) => void): (() => void) => {
    crossPseudoIdListeners.add(callback);

    return () => crossPseudoIdListeners.delete(callback);
};

export {experiment};
import {Source} from "@amplitude/experiment-js-client";
import {add, experiment, flush, getDeviceId, getSessionId, getUserId, Identify, identify, initAll, reset, setUserId, Types} from "@amplitude/unified";
import type {UnifiedOptions} from "@amplitude/unified/lib/cjs/unified";

import {IAmplitudeExperimentVariants} from "../types/IAmplitudeExperimentVariants";
import {defaultAmplitudeLogger, IAmplitudeLogger} from "../types/IAmplitudeLogger";
import {initAmplitudePageLeaveFlush} from "./init_amplitude_page_leave_flush";

type AmplitudeUserPropertyValue = Types.ValidPropertyType | null;

const amplitudeState = {
    isInitialized: false,
    initializationPromise: null as Promise<void> | null,
    pendingUserId: undefined as string | undefined,
    hasPendingUserId: false,
    pendingUserProperties: null as Record<string, AmplitudeUserPropertyValue> | null
};

const applyAmplitudeUserId = (userId: string | undefined): void => setUserId(userId);

const applyAmplitudeUserProperties = (properties: Record<string, AmplitudeUserPropertyValue>): void => {
    const identifyObj = new Identify();

    for (const [key, value] of Object.entries(properties)) {
        if (value === null) {
            identifyObj.unset(key);
            continue;
        }

        identifyObj.set(key, value);
    }

    identify(identifyObj);
};

const flushPendingAmplitudeUserState = (): void => {
    if (!amplitudeState.isInitialized) return;

    if (amplitudeState.hasPendingUserId) {
        applyAmplitudeUserId(amplitudeState.pendingUserId);
        amplitudeState.hasPendingUserId = false;
    }

    if (amplitudeState.pendingUserProperties) {
        applyAmplitudeUserProperties(amplitudeState.pendingUserProperties);
        amplitudeState.pendingUserProperties = null;
    }
};

const ALLOWED_AMPLITUDE_ENVIRONMENTS = ["production", "stage:top", "stage:dev", "stage:ovh", "local"] as const;
const APP_ENVIRONMENT_PROPERTY = "app_environment";
const AMPLITUDE_ENVIRONMENT_PLUGIN_NAME = "app-environment-enrichment";

const CROSS_PSEUDO_ID_PROPERTY = "cross_pseudo_id";
const CROSS_PSEUDO_ID_PLUGIN_NAME = "cross-pseudo-id-enrichment";

let lastCrossPseudoId: string | null = null;
const crossPseudoIdListeners = new Set<(crossPseudoId: string | null) => void>();

// Amplitude session id alone is the session start timestamp (ms), so two devices starting a session
// in the same millisecond would share it - the device id (random UUID) makes the value unique.
const buildCrossPseudoId = (deviceId: string | undefined, sessionId: number | undefined): string | null => {
    if (deviceId === undefined || sessionId === undefined) {
        return null;
    }

    return `${deviceId}_${sessionId}`;
};

const applyCrossPseudoId = (crossPseudoId: string | null): void => {
    const identifyObj = new Identify();
    if (crossPseudoId === null) {
        identifyObj.unset(CROSS_PSEUDO_ID_PROPERTY);
    } else {
        identifyObj.set(CROSS_PSEUDO_ID_PROPERTY, crossPseudoId);
    }
    identify(identifyObj);

    crossPseudoIdListeners.forEach((listener) => listener(crossPseudoId));
};

// Amplitude has no native "session changed" event - the enrichment hook (fired on every
// tracked event) is the only place we can detect a session or device rotation (including it disappearing).
const crossPseudoIdPlugin: Parameters<typeof add>[0] = {
    name: CROSS_PSEUDO_ID_PLUGIN_NAME,
    type: "enrichment",
    async execute(event) {
        const crossPseudoId = buildCrossPseudoId(getDeviceId() ?? undefined, getSessionId() ?? undefined);

        if (crossPseudoId !== lastCrossPseudoId) {
            lastCrossPseudoId = crossPseudoId;
            applyCrossPseudoId(crossPseudoId);
        }

        return event;
    }
};

type TAmplitudeEnvironment = (typeof ALLOWED_AMPLITUDE_ENVIRONMENTS)[number];

interface IInitAmplitudeParams {
    amplitudeApiKey?: string;
    deploymentKey?: string;
    initialVariants?: IAmplitudeExperimentVariants;
    userId?: string;
    isBrowser?: boolean;
    logger?: IAmplitudeLogger;
    environment?: string;
}

const validateAmplitudeEnvironment = (environment: string): TAmplitudeEnvironment => {
    if (ALLOWED_AMPLITUDE_ENVIRONMENTS.includes(environment as TAmplitudeEnvironment)) {
        return environment as TAmplitudeEnvironment;
    }

    throw new Error(`Invalid Amplitude environment "${environment}". Allowed values: ${ALLOWED_AMPLITUDE_ENVIRONMENTS.join(", ")}`);
};

export const initAmplitude = ({
    amplitudeApiKey,
    deploymentKey,
    initialVariants,
    userId,
    isBrowser = false,
    logger: _logger = defaultAmplitudeLogger,
    environment
}: IInitAmplitudeParams): void => {
    if (!amplitudeApiKey) return; // no key, silent exit

    if (process.env.EXEC_ENV !== "browser" && !isBrowser) return;

    // initialize only once
    if (amplitudeState.isInitialized || amplitudeState.initializationPromise) return;

    const validatedEnvironment = environment ? validateAmplitudeEnvironment(environment) : undefined;
    const options: UnifiedOptions = {
        serverZone: "EU",
        analytics: {
            autocapture: true
        }
    };

    if (deploymentKey) {
        options.experiment = {
            deploymentKey,
            initialVariants: initialVariants ?? {},
            source: Source.InitialVariants
        };
    }

    if (validatedEnvironment) {
        const environmentPlugin: Parameters<typeof add>[0] = {
            name: AMPLITUDE_ENVIRONMENT_PLUGIN_NAME,
            type: "enrichment",
            async execute(event) {
                event.event_properties = {
                    ...(event.event_properties ?? {}),
                    [APP_ENVIRONMENT_PROPERTY]: validatedEnvironment
                };

                return event;
            }
        };

        add(environmentPlugin);
    }

    add(crossPseudoIdPlugin);

    initAmplitudePageLeaveFlush();

    amplitudeState.initializationPromise = Promise.resolve(initAll(amplitudeApiKey, options))
        .then(() => {
            amplitudeState.isInitialized = true;
            flushPendingAmplitudeUserState();
        })
        .catch((error) => {
            amplitudeState.initializationPromise = null;
            throw error;
        });

    if (userId) setAmplitudeUserId(userId);

    if (validatedEnvironment) {
        const identifyObj = new Identify();

        identifyObj.set(APP_ENVIRONMENT_PROPERTY, validatedEnvironment);
        identify(identifyObj);
    }
};

export const waitForAmplitudeInitialization = async (): Promise<void> => {
    await amplitudeState.initializationPromise;
};

export const setAmplitudeUserId = (userId: string | undefined): void => {
    amplitudeState.pendingUserId = userId;
    amplitudeState.hasPendingUserId = true;

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserId(userId);
        amplitudeState.hasPendingUserId = false;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserId(userId);
};

export const setAmplitudeUserProperties = (properties: Record<string, AmplitudeUserPropertyValue>): void => {
    amplitudeState.pendingUserProperties = {...(amplitudeState.pendingUserProperties ?? {}), ...properties};

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserProperties(properties);
        amplitudeState.pendingUserProperties = null;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserProperties(properties);
};

export const flushAmplitude = async (): Promise<void> => {
    if (!amplitudeState.isInitialized) return;

    await flush().promise;
};

export const resetAmplitude = (): void => {
    amplitudeState.hasPendingUserId = false;
    amplitudeState.pendingUserProperties = null;

    if (amplitudeState.isInitialized) reset();
};

export const getAmplitudeUserId = (): string | undefined => {
    if (!amplitudeState.isInitialized) return undefined;

    return getUserId() ?? undefined;
};

export const getAmplitudeDeviceId = (): string | undefined => {
    if (!amplitudeState.isInitialized && !amplitudeState.initializationPromise) {
        return undefined;
    }

    return getDeviceId() ?? undefined;
};

export const getAmplitudeSessionId = (): number | undefined => {
    if (!amplitudeState.isInitialized) {
        return undefined;
    }

    return getSessionId() ?? undefined;
};

export const getCrossPseudoId = (): string | undefined => {
    if (!amplitudeState.isInitialized) {
        return undefined;
    }

    return buildCrossPseudoId(getDeviceId() ?? undefined, getSessionId() ?? undefined) ?? undefined;
};

export const onCrossPseudoIdChange = (callback: (crossPseudoId: string | null) => void): (() => void) => {
    crossPseudoIdListeners.add(callback);

    return () => crossPseudoIdListeners.delete(callback);
};

export {experiment};
